import { useState, useEffect } from "react";
import { Image, FileText, Palette, Save, Upload, CreditCard, Plus, Trash2, Loader2, Recycle, MapPin, Settings } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { getContent, createSubscriptionPlan, getSubscriptionPlans, deleteSubscriptionPlan, updateSubscriptionPlan, getWasteItems, createWasteItem, updateWasteItem, deleteWasteItem, getServiceAreas, createServiceArea, deleteServiceArea, getAppUpdates, createAppUpdate } from "@/services/api";

export const ContentManagement = () => {
  const [bannerTitle, setBannerTitle] = useState("Get 20% Off Premium Subscription!");
  const [bannerDescription, setBannerDescription] = useState("Upgrade now and save big");
  const [offerText, setOfferText] = useState("Special offer: First month free for new users");
  const [banners, setBanners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [wasteItems, setWasteItems] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [appUpdates, setAppUpdates] = useState([]);
  const [isFetchingPlans, setIsFetchingPlans] = useState(false);
  const [isFetchingWasteItems, setIsFetchingWasteItems] = useState(false);
  const [isFetchingServiceAreas, setIsFetchingServiceAreas] = useState(false);
  const [isWasteDialogOpen, setIsWasteDialogOpen] = useState(false);
  const [isWasteEditMode, setIsWasteEditMode] = useState(false);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingUpdates, setIsFetchingUpdates] = useState(false);

  // New version state
  const [newUpdate, setNewUpdate] = useState({
    android_version: "",
    ios_version: "",
    force_update: false,
    android_url: "",
    ios_url: "",
    description: ""
  });

  const initialWasteState = {
    name: "",
    category: "general",
    coins_per_kg: 0,
    min_weight_kg: 0,
    image: ""
  };

  const initialServiceAreaState = {
    pincode: "",
    area_name: ""
  };
  const [newServiceArea, setNewServiceArea] = useState(initialServiceAreaState);
  const [newWasteItem, setNewWasteItem] = useState(initialWasteState);

  const fetchWasteItems = async () => {
    setIsFetchingWasteItems(true);
    try {
      const response = await getWasteItems();
      if (response.success) {
        setWasteItems(response.items || []);
      }
    } catch (error) {
      console.error("Error fetching waste items:", error);
    } finally {
      setIsFetchingWasteItems(false);
    }
  };

  const fetchServiceAreas = async () => {
    setIsFetchingServiceAreas(true);
    try {
      const response = await getServiceAreas();
      if (response.success) {
        setServiceAreas(response.areas || []);
      }
    } catch (error) {
      console.error("Error fetching service areas:", error);
    } finally {
      setIsFetchingServiceAreas(false);
    }
  };

  const handleSaveServiceArea = async () => {
    if (!newServiceArea.pincode || !newServiceArea.area_name) {
      toast.error("Please fill all fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await createServiceArea(newServiceArea);
      toast.success("Service area added successfully");
      setIsServiceDialogOpen(false);
      setNewServiceArea(initialServiceAreaState);
      fetchServiceAreas();
    } catch (error) {
      toast.error("Failed to add service area");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteServiceArea = async (id) => {
    try {
      await deleteServiceArea(id);
      toast.success("Service area deleted successfully");
      fetchServiceAreas();
    } catch (error) {
      toast.error("Failed to delete service area");
    }
  };

  const handleWasteEditClick = (item) => {
    setNewWasteItem(item);
    setIsWasteEditMode(true);
    setIsWasteDialogOpen(true);
  };

  const handleSaveWasteItem = async () => {
    setIsSubmitting(true);
    try {
      if (isWasteEditMode) {
        await updateWasteItem(newWasteItem.id, newWasteItem);
        toast.success("Waste item updated successfully");
      } else {
        await createWasteItem(newWasteItem);
        toast.success("Waste item created successfully");
      }
      setIsWasteDialogOpen(false);
      fetchWasteItems();
    } catch (error) {
      toast.error("Failed to save waste item");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteWasteItem = async (id) => {
    try {
      await deleteWasteItem(id);
      toast.success("Waste item deleted successfully");
      fetchWasteItems();
    } catch (error) {
      toast.error("Failed to delete waste item");
    }
  };

  const fetchAppUpdates = async () => {
    setIsFetchingUpdates(true);
    try {
      const response = await getAppUpdates();
      if (response.success) {
        setAppUpdates(response.updates || []);
      }
    } catch (error) {
      console.error("Error fetching app updates:", error);
    } finally {
      setIsFetchingUpdates(false);
    }
  };

  const handlePushUpdate = async () => {
    if (!newUpdate.android_version || !newUpdate.ios_version) {
      toast.error("Please fill version numbers");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await createAppUpdate(newUpdate);
      if (response.success) {
        toast.success("App update pushed successfully");
        setNewUpdate({
          android_version: "",
          ios_version: "",
          force_update: false,
          android_url: "",
          ios_url: "",
          description: ""
        });
        fetchAppUpdates();
      }
    } catch (error) {
      toast.error("Failed to push update");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const data = await getContent();
        if (data && data.success) {
          const content = data.content;
          setBanners(content.filter(c => c.type === 'banner'));
          setOffers(content.filter(c => c.type === 'offer'));
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      }
      fetchWasteItems();
      fetchServiceAreas();
      fetchAppUpdates();
    };
    fetchInitialData();
  }, []);

  const handleSave = (section) => {
    toast.success(`${section} updated successfully!`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Manage App</h1>
        <p className="text-muted-foreground mt-1">Manage app content, banners, offers, and branding</p>
      </div>

      <Tabs defaultValue="banners" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="banners">
            <Image className="w-4 h-4 mr-2" />
            Banners
          </TabsTrigger>
          <TabsTrigger value="offers">
            <FileText className="w-4 h-4 mr-2" />
            Offers
          </TabsTrigger>
          <TabsTrigger value="service-areas">
            <MapPin className="w-4 h-4 mr-2" />
            Service Area
          </TabsTrigger>
          <TabsTrigger value="waste-items">
            <Recycle className="w-4 h-4 mr-2" />
            Waste Items
          </TabsTrigger>
          <TabsTrigger value="manage-version">
            <Settings className="w-4 h-4 mr-2" />
            Manage Version
          </TabsTrigger>
          <TabsTrigger value="branding">
            <Palette className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
        </TabsList>

        {/* Banners Tab */}
        <TabsContent value="banners" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>App Banners</CardTitle>
              <CardDescription>Manage promotional banners displayed in the mobile apps</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="banner-title">Banner Title</Label>
                  <Input
                    id="banner-title"
                    value={bannerTitle}
                    onChange={(e) => setBannerTitle(e.target.value)}
                    placeholder="Enter banner title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="banner-description">Banner Description</Label>
                  <Input
                    id="banner-description"
                    value={bannerDescription}
                    onChange={(e) => setBannerDescription(e.target.value)}
                    placeholder="Enter banner description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="banner-image">Banner Image</Label>
                  <div className="flex gap-4">
                    <Input id="banner-image" type="file" accept="image/*" className="flex-1" />
                    <Button variant="outline">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Recommended size: 1200x400px</p>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-8 bg-gradient-to-r from-primary/10 to-secondary/10">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-foreground mb-2">{bannerTitle}</h3>
                    <p className="text-muted-foreground">{bannerDescription}</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => handleSave("Banner")}
                className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-primary-foreground"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Banner
              </Button>
            </CardContent>
          </Card>

          {/* Active Banners */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Active Banners</CardTitle>
              <CardDescription>Currently displayed banners in the apps</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">Welcome Offer</h4>
                    <p className="text-sm text-muted-foreground mt-1">First month free for new subscriptions</p>
                    <p className="text-xs text-muted-foreground mt-2">Active since: Jun 1, 2024</p>
                  </div>
                  <Button variant="outline" size="sm">Edit</Button>
                </div>
              </div>
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">Premium Upgrade</h4>
                    <p className="text-sm text-muted-foreground mt-1">Get 20% off on premium plans</p>
                    <p className="text-xs text-muted-foreground mt-2">Active since: May 15, 2024</p>
                  </div>
                  <Button variant="outline" size="sm">Edit</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Special Offers</CardTitle>
              <CardDescription>Create and manage promotional offers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="offer-text">Offer Text</Label>
                <Textarea
                  id="offer-text"
                  value={offerText}
                  onChange={(e) => setOfferText(e.target.value)}
                  placeholder="Enter offer details"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="offer-start">Start Date</Label>
                  <Input id="offer-start" type="date" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="offer-end">End Date</Label>
                  <Input id="offer-end" type="date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="offer-discount">Discount Percentage</Label>
                <Input id="offer-discount" type="number" placeholder="20" />
              </div>
              <Button
                onClick={() => handleSave("Offer")}
                className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-primary-foreground"
              >
                <Save className="w-4 h-4 mr-2" />
                Create Offer
              </Button>
            </CardContent>
          </Card>

          {/* Active Offers */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Active Offers</CardTitle>
              <CardDescription>Currently running promotional offers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">20% Off Premium</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Get 20% discount on premium subscription for 3 months
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Edit</Button>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Valid: Jun 1 - Jun 30, 2024</span>
                  <span className="text-primary font-medium">152 users redeemed</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Waste Items Tab */}
        <TabsContent value="service-areas" className="space-y-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Service Areas</CardTitle>
                <CardDescription>Manage locations where service is available</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchServiceAreas} disabled={isFetchingServiceAreas}>
                {isFetchingServiceAreas ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isFetchingServiceAreas ? (
                  Array(3).fill(0).map((_, i) => (
                    <Card key={i} className="border-border animate-pulse">
                      <div className="h-24 bg-muted rounded-lg" />
                    </Card>
                  ))
                ) : (
                  <>
                    {serviceAreas.map((area) => (
                      <Card key={area.id} className="border-border flex flex-col">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-lg">{area.area_name}</CardTitle>
                            <MapPin className="w-4 h-4 text-primary" />
                          </div>
                        </CardHeader>
                        <CardContent className="pb-4 space-y-2 flex-grow">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Pincode:</span>
                            <span className="font-semibold">{area.pincode}</span>
                          </div>
                          <div className="flex justify-end pt-4">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Service Area?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will remove "{area.area_name}" from service locations.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteServiceArea(area.id)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <Dialog open={isServiceDialogOpen} onOpenChange={setIsServiceDialogOpen}>
                      <DialogTrigger asChild>
                        <div className="border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center p-6 text-center space-y-2 hover:bg-muted/50 transition-colors cursor-pointer min-h-[120px]">
                          <Plus className="w-8 h-8 text-primary" />
                          <h4 className="font-semibold">Add New Area</h4>
                        </div>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Service Area</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="space-y-2">
                            <Label>Area Name</Label>
                            <Input value={newServiceArea.area_name} onChange={(e) => setNewServiceArea({...newServiceArea, area_name: e.target.value})} placeholder="e.g. Downtown" />
                          </div>
                          <div className="space-y-2">
                            <Label>Pincode</Label>
                            <Input value={newServiceArea.pincode} onChange={(e) => setNewServiceArea({...newServiceArea, pincode: e.target.value})} placeholder="e.g. 600001" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsServiceDialogOpen(false)}>Cancel</Button>
                          <Button onClick={handleSaveServiceArea} disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Area"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="waste-items" className="space-y-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Items We Collect</CardTitle>
                <CardDescription>Manage waste categories and prices displayed in the app</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchWasteItems} disabled={isFetchingWasteItems}>
                {isFetchingWasteItems ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isFetchingWasteItems ? (
                  Array(3).fill(0).map((_, i) => (
                    <Card key={i} className="border-border animate-pulse">
                      <div className="h-32 bg-muted rounded-lg" />
                    </Card>
                  ))
                ) : (
                  <>
                    {wasteItems.map((item) => (
                      <Card key={item.id} className="border-border flex flex-col">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-lg">{item.name}</CardTitle>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full capitalize">{item.category}</span>
                          </div>
                        </CardHeader>
                        <CardContent className="pb-4 space-y-2 flex-grow">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Price:</span>
                            <span className="font-semibold text-success">₹{item.coins_per_kg}/kg</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Min Weight:</span>
                            <span className="font-semibold">{item.min_weight_kg} kg</span>
                          </div>
                          <div className="flex gap-2 pt-4">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => handleWasteEditClick(item)}>Edit</Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Waste Item?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will remove "{item.name}" from the app.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteWasteItem(item.id)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <Dialog open={isWasteDialogOpen} onOpenChange={(open) => {
                      setIsWasteDialogOpen(open);
                      if (!open) {
                        setIsWasteEditMode(false);
                        setNewWasteItem(initialWasteState);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <div className="border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center p-6 text-center space-y-2 hover:bg-muted/50 transition-colors cursor-pointer min-h-[160px]">
                          <Plus className="w-8 h-8 text-primary" />
                          <h4 className="font-semibold">Add New Item</h4>
                        </div>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{isWasteEditMode ? "Edit Waste Item" : "Add Waste Item"}</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="space-y-2">
                            <Label>Item Name</Label>
                            <Input value={newWasteItem.name} onChange={(e) => setNewWasteItem({...newWasteItem, name: e.target.value})} placeholder="e.g. Newspaper" />
                          </div>
                          <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={newWasteItem.category} onValueChange={(val) => setNewWasteItem({...newWasteItem, category: val})}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="paper">Paper</SelectItem>
                                <SelectItem value="plastic">Plastic</SelectItem>
                                <SelectItem value="metal">Metal</SelectItem>
                                <SelectItem value="glass">Glass</SelectItem>
                                <SelectItem value="cardboard">Cardboard</SelectItem>
                                <SelectItem value="electronic">E-Waste</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Price per Kg (₹)</Label>
                              <Input type="number" value={newWasteItem.coins_per_kg} onChange={(e) => setNewWasteItem({...newWasteItem, coins_per_kg: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                              <Label>Min Weight (kg)</Label>
                              <Input type="number" value={newWasteItem.min_weight_kg} onChange={(e) => setNewWasteItem({...newWasteItem, min_weight_kg: e.target.value})} />
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsWasteDialogOpen(false)}>Cancel</Button>
                          <Button onClick={handleSaveWasteItem} disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isWasteEditMode ? "Update" : "Add")}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manage Version Tab */}
        <TabsContent value="manage-version" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Manage App Version</CardTitle>
              <CardDescription>
                Control app versions and force update settings. 
                <span className="block mt-1 font-semibold text-amber-600 text-sm italic">Note: Latest version should be greater than the previous version.</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Android Version</Label>
                  <Input 
                    placeholder="e.g. 1.0.5" 
                    value={newUpdate.android_version}
                    onChange={(e) => setNewUpdate({...newUpdate, android_version: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>iOS Version</Label>
                  <Input 
                    placeholder="e.g. 1.0.5" 
                    value={newUpdate.ios_version}
                    onChange={(e) => setNewUpdate({...newUpdate, ios_version: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Android Update URL</Label>
                  <Input 
                    placeholder="Play Store Link" 
                    value={newUpdate.android_url}
                    onChange={(e) => setNewUpdate({...newUpdate, android_url: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>iOS Update URL</Label>
                  <Input 
                    placeholder="App Store Link" 
                    value={newUpdate.ios_url}
                    onChange={(e) => setNewUpdate({...newUpdate, ios_url: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Update Description</Label>
                <Textarea 
                  placeholder="What's new in this version?" 
                  rows={3}
                  value={newUpdate.description}
                  onChange={(e) => setNewUpdate({...newUpdate, description: e.target.value})}
                />
              </div>

              <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
                <Switch 
                  id="force-update" 
                  checked={newUpdate.force_update}
                  onCheckedChange={(checked) => setNewUpdate({...newUpdate, force_update: checked})}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="force-update" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Force Update
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Users will be required to update the app to continue using it.
                  </p>
                </div>
              </div>

              <Button 
                className="w-full sm:w-auto" 
                onClick={handlePushUpdate}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Push Update
              </Button>
            </CardContent>
          </Card>

          {/* Update History */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Update History</CardTitle>
              <CardDescription>Previous app version releases</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isFetchingUpdates ? (
                  Array(2).fill(0).map((_, i) => (
                    <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
                  ))
                ) : appUpdates.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No update history found.</p>
                ) : (
                  appUpdates.map((update) => (
                    <div key={update.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-foreground">v{update.android_version} (Android) / v{update.ios_version} (iOS)</h4>
                            {update.force_update && <span className="text-[10px] bg-destructive/10 text-destructive px-2 py-0.5 rounded-full font-bold uppercase">Force</span>}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{update.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{new Date(update.created_at).toLocaleDateString()} {new Date(update.created_at).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>Customize the app's color scheme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex gap-4">
                    <Input
                      id="primary-color"
                      type="color"
                      defaultValue="#4CAF50"
                      className="w-20 h-11"
                    />
                    <Input defaultValue="#4CAF50" className="flex-1" readOnly />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary-color">Secondary Color</Label>
                  <div className="flex gap-4">
                    <Input
                      id="secondary-color"
                      type="color"
                      defaultValue="#8BC34A"
                      className="w-20 h-11"
                    />
                    <Input defaultValue="#8BC34A" className="flex-1" readOnly />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accent-color">Accent Color</Label>
                  <div className="flex gap-4">
                    <Input
                      id="accent-color"
                      type="color"
                      defaultValue="#66BB6A"
                      className="w-20 h-11"
                    />
                    <Input defaultValue="#66BB6A" className="flex-1" readOnly />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="background-color">Background Color</Label>
                  <div className="flex gap-4">
                    <Input
                      id="background-color"
                      type="color"
                      defaultValue="#F5F5F5"
                      className="w-20 h-11"
                    />
                    <Input defaultValue="#F5F5F5" className="flex-1" readOnly />
                  </div>
                </div>
              </div>
              <Button
                onClick={() => handleSave("Brand Colors")}
                className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-primary-foreground"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Colors
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle>Typography</CardTitle>
              <CardDescription>Customize fonts used in the app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="heading-font">Heading Font</Label>
                  <Input id="heading-font" defaultValue="Space Grotesk" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body-font">Body Font</Label>
                  <Input id="body-font" defaultValue="Inter" />
                </div>
              </div>
              <Button
                onClick={() => handleSave("Typography")}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Fonts
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle>App Assets</CardTitle>
              <CardDescription>Upload app icons and store screenshots</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>App Icon</Label>
                <div className="flex gap-4">
                  <Input type="file" accept="image/*" className="flex-1" />
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Recommended size: 1024x1024px</p>
              </div>
              <div className="space-y-2">
                <Label>Play Store Screenshots</Label>
                <div className="flex gap-4">
                  <Input type="file" accept="image/*" multiple className="flex-1" />
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Upload 3-5 screenshots</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentManagement;
